import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { BACKEND } from "@/lib/api";
import { PinFace, pinClass, pinStyle } from "@/threedvista/pinStyles";

const RAD = Math.PI / 180;
const dir = (y, p) => new THREE.Vector3(Math.cos(p * RAD) * Math.sin(y * RAD), Math.sin(p * RAD), Math.cos(p * RAD) * Math.cos(y * RAD));
const ease = (k) => (k < 0.5 ? 2 * k * k : 1 - (-2 * k + 2) ** 2 / 2);
export const panoUrl = (tid, name, w = 4096) => `${BACKEND}/api/public/tdv/${tid}/g360-pano/${encodeURIComponent(name)}?w=${w}`;

function useViewer(mount) {
  const v = useRef(null);
  useEffect(() => {
    const el = mount.current;
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    el.appendChild(renderer.domElement);
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1100);
    const scene = new THREE.Scene();
    const geo = new THREE.SphereGeometry(500, 64, 40);
    geo.scale(-1, 1, 1);
    const mat = new THREE.MeshBasicMaterial({ color: 0x111111 });
    scene.add(new THREE.Mesh(geo, mat));
    const s = { renderer, camera, scene, mat, yaw: 0, pitch: 0, fov: 75, anim: null, w: 1, h: 1 };
    const size = () => { s.w = el.clientWidth || 1; s.h = el.clientHeight || 1; renderer.setSize(s.w, s.h); camera.aspect = s.w / s.h; camera.updateProjectionMatrix(); };
    size();
    const ro = new ResizeObserver(size);
    ro.observe(el);
    s.look = () => { camera.fov = s.fov; camera.updateProjectionMatrix(); camera.lookAt(dir(s.yaw, s.pitch)); };
    s.project = (yaw, pitch) => {
      const d = dir(yaw, pitch);
      if (d.dot(dir(s.yaw, s.pitch)) < 0.25) return null;
      const p = d.multiplyScalar(100).project(camera);
      return { x: (p.x + 1) / 2 * s.w, y: (1 - p.y) / 2 * s.h };
    };
    s.unproject = (x, y) => {
      const ray = new THREE.Raycaster();
      ray.setFromCamera(new THREE.Vector2((x / s.w) * 2 - 1, -(y / s.h) * 2 + 1), camera);
      const d = ray.ray.direction;
      return { yaw: +(Math.atan2(d.x, d.z) / RAD).toFixed(2), pitch: +(Math.asin(Math.max(-1, Math.min(1, d.y))) / RAD).toFixed(2) };
    };
    s.animate = (to, ms) => new Promise((done) => { s.anim = { from: { yaw: s.yaw, pitch: s.pitch, fov: s.fov }, to: { yaw: s.yaw, pitch: s.pitch, fov: s.fov, ...to }, t0: performance.now(), ms, done }; });
    v.current = s;
    return () => { ro.disconnect(); renderer.dispose(); geo.dispose(); mat.map?.dispose(); mat.dispose(); el.removeChild(renderer.domElement); v.current = null; };
  }, [mount]);
  return v;
}

function useDrag(v, layer) {
  useEffect(() => {
    const el = layer.current;
    let last = null;
    const down = (e) => { if (e.target.closest(".tdv-pin")) return; last = { x: e.clientX, y: e.clientY }; v.current.anim = null; el.setPointerCapture?.(e.pointerId); };
    const move = (e) => {
      const s = v.current;
      if (!last || !s) return;
      const k = s.fov / s.h;
      s.yaw += (e.clientX - last.x) * k;
      s.pitch = Math.max(-85, Math.min(85, s.pitch + (e.clientY - last.y) * k));
      last = { x: e.clientX, y: e.clientY };
    };
    const up = () => { last = null; };
    const wheel = (e) => { e.preventDefault(); const s = v.current; if (s) s.fov = Math.max(35, Math.min(95, s.fov + e.deltaY * 0.04)); };
    el.addEventListener("pointerdown", down);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    el.addEventListener("wheel", wheel, { passive: false });
    return () => { el.removeEventListener("pointerdown", down); window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); el.removeEventListener("wheel", wheel); };
  }, [v, layer]);
}

export function PanoStage({ giraffe, hotspots = [], placing = false, onReady, onScene, onPlace, onPin, activeId, numbers = {} }) {
  const mount = useRef(null);
  const layer = useRef(null);
  const pins = useRef({});
  const [fade, setFade] = useState(true);
  const v = useViewer(mount);
  useDrag(v, layer);
  const live = useRef({});
  Object.assign(live.current, { hotspots, onScene, onReady });
  const tid = giraffe.id;
  const keyStr = (giraffe.g360_scenes || []).join(",");

  useEffect(() => {
    const list = keyStr ? keyStr.split(",").map((id, i) => ({ id, label: `Room ${i + 1}`, index: i, kind: "Panorama", thumb: panoUrl(tid, id, 320) })) : [];
    const loader = new THREE.TextureLoader();
    const cur = { i: -1, token: 0 };
    let raf;
    const show = (i) => new Promise((done) => {
      const sc = list[i];
      if (!sc || !v.current) { done(); return; }
      const token = ++cur.token;
      setFade(true);
      loader.load(panoUrl(tid, sc.id), (tex) => {
        const s = v.current;
        if (!s || token !== cur.token) { tex.dispose(); done(); return; }
        tex.colorSpace = THREE.SRGBColorSpace;
        s.mat.map?.dispose();
        s.mat.map = tex;
        s.mat.color.set(0xffffff);
        s.mat.needsUpdate = true;
        cur.i = i;
        live.current.curId = sc.id;
        setFade(false);
        live.current.onScene?.(i, sc);
        done();
      }, undefined, () => { setFade(false); done(); });
    });
    const api = {
      giraffe: true,
      setMediaByIndex: (i) => show(i),
      glide: async (i, ms) => {
        const s = v.current;
        if (!s) return;
        await s.animate({ fov: 55 }, 600);
        await show(i);
        if (!v.current) return;
        v.current.fov = 80;
        await v.current.animate({ yaw: v.current.yaw + 50, fov: 70 }, Math.max(1500, ms - 600));
      },
    };
    live.current.onReady?.(api, list);
    if (list.length) show(0);
    const loop = (now) => {
      const s = v.current;
      if (s) {
        if (s.anim) {
          const a = s.anim;
          const k = Math.min(1, (now - a.t0) / a.ms);
          const e = ease(k);
          ["yaw", "pitch", "fov"].forEach((p) => { s[p] = a.from[p] + (a.to[p] - a.from[p]) * e; });
          if (k >= 1) { s.anim = null; a.done(); }
        }
        s.look();
        s.renderer.render(s.scene, s.camera);
        live.current.hotspots.forEach((h) => {
          const el = pins.current[h.id];
          if (!el) return;
          const p = h.media_id === list[cur.i]?.id ? s.project(h.yaw, h.pitch) : null;
          el.style.display = p ? "flex" : "none";
          if (p) el.style.transform = `translate(${p.x}px, ${p.y}px) translate(-50%, -50%)`;
        });
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); cur.token++; };
  }, [keyStr, tid, v]);

  const place = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const sc = (giraffe.g360_scenes || []);
    const s = v.current;
    if (!s) return;
    onPlace?.({ media_id: live.current.curId || sc[0], ...s.unproject(e.clientX - r.left, e.clientY - r.top) });
  };

  return (
    <div className="absolute inset-0 overflow-hidden bg-slate-950 select-none" data-testid="tdv-stage">
      <div ref={mount} className="absolute inset-0" data-testid="g360-pano-canvas" />
      <div ref={layer} className="absolute inset-0 cursor-grab active:cursor-grabbing touch-none" data-testid="g360-pano-drag" />
      <div className={`absolute inset-0 bg-slate-950 pointer-events-none transition-opacity duration-500 ${fade ? "opacity-70" : "opacity-0"}`} />
      <div className="absolute inset-0 pointer-events-none">
        {hotspots.map((h) => (
          <button key={h.id} ref={(el) => { pins.current[h.id] = el; }} type="button" onClick={() => onPin?.(h)}
            className={`tdv-pin absolute left-0 top-0 hidden pointer-events-auto items-center justify-center transition-opacity ${pinClass(h)} ${activeId === h.id ? "ring-4 ring-white rounded-full" : ""}`}
            style={pinStyle(h)} title={h.title} data-testid={`tdv-pin-${h.id}`}>
            <PinFace h={h} />
            {numbers[h.id] && <span className="absolute -top-1.5 -right-1.5 min-w-[1.25rem] h-5 px-1 rounded-full bg-white text-slate-900 text-[11px] font-bold grid place-items-center shadow" data-testid={`tdv-pin-number-${h.id}`}>{numbers[h.id]}</span>}
          </button>
        ))}
      </div>
      {placing && <div className="absolute inset-0 cursor-crosshair bg-black/10" onClick={place} data-testid="tdv-place-layer" />}
    </div>
  );
}
